import { html } from '@shared/utils/rendering/html';
import { useState } from 'preact/hooks';
import Button from '@shared/components/buttons';
import Modal from '@shared/components/modals';
import Checkbox from '@shared/components/forms/form-components/checkboxes';
import { SarbSelfAssessmentService } from '@core/utils/api/sarb-self-assessment.service';
import { toast } from 'react-toastify';
import writeXlsxFile from 'write-excel-file/browser';
import { buildConsolidatedRows } from './grouping';
import {
    SarbReportingMonths,
    ExcelHeaderStyle,
} from '@core/constants/sarb-self-assessment.constants';

const HEADER = ExcelHeaderStyle;

const buildYearSheet = (allEntities, septReport, decReport) => {
    const rows = buildConsolidatedRows({
        allEntities,
        septemberReport: septReport,
        decemberReport: decReport,
    });

    return {
        columns: [{ width: 36 }, { width: 20 }, { width: 20 }],
        data: [
            [
                { value: 'Legal Entity', ...HEADER },
                { value: 'September Rating', ...HEADER },
                { value: 'December Rating', ...HEADER },
            ],
            ...rows.map((row) => [
                { value: row.name },
                { value: row.septemberRating },
                { value: row.decemberRating },
            ]),
        ],
    };
};

export const ConsolidatedExportModal = ({
    isVisible,
    onClose,
    availableYears = [],
    allPeriods = [],
    allEntities = [],
}) => {
    const [selectedYears, setSelectedYears] = useState([]);
    const [isExporting, setIsExporting] = useState(false);

    const toggleYear = (year) => {
        setSelectedYears((prev) =>
            prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
        );
    };

    const selectAll = () => setSelectedYears([...availableYears]);
    const clearAll = () => setSelectedYears([]);

    const findPeriodId = (year, month) =>
        allPeriods.find((p) => {
            const d = new Date(p.endDate);
            return d.getFullYear() === year && d.getMonth() + 1 === month;
        })?.value ?? null;

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const sortedYears = [...selectedYears].sort((a, b) => a - b);
            const sheetsData = [];
            const sheetsColumns = [];
            const sheetNames = [];

            for (const year of sortedYears) {
                const septId = findPeriodId(year, SarbReportingMonths.September);
                const decId = findPeriodId(year, SarbReportingMonths.December);

                const [septReport, decReport] = await Promise.all([
                    septId
                        ? SarbSelfAssessmentService.getConsolidatedReport(septId).catch(() => null)
                        : Promise.resolve(null),
                    decId
                        ? SarbSelfAssessmentService.getConsolidatedReport(decId).catch(() => null)
                        : Promise.resolve(null),
                ]);

                const sheet = buildYearSheet(allEntities, septReport, decReport);
                sheetsData.push(sheet.data);
                sheetsColumns.push(sheet.columns);
                sheetNames.push(String(year));
            }

            await writeXlsxFile(
                sheetNames.map((name, i) => ({
                    sheet: name,
                    data: sheetsData[i],
                    columns: sheetsColumns[i],
                }))
            ).toFile('consolidated-sarb-report.xlsx');

            toast.success(
                `Exported ${sortedYears.length} year${sortedYears.length > 1 ? 's' : ''} to Excel.`
            );
            onClose();
        } catch {
            toast.error('Export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    return html`
        <${Modal}
            id="consolidated-export-modal"
            header=${{ title: { text: 'Export' } }}
            footer=${html`
                <${Button}
                    buttonType="secondary"
                    label=${{ text: 'Cancel' }}
                    onClick=${onClose}
                    disabled=${isExporting}
                />
                <${Button}
                    buttonType="primary"
                    label=${{ text: isExporting ? 'Exporting...' : 'Export' }}
                    onClick=${handleExport}
                    disabled=${!selectedYears.length || isExporting}
                />
            `}
            options=${{ isVisible, onCloseEvent: onClose }}
        >
            <p class="fw-bold mb-3">Select the year(s) you want to export</p>

            <div class="d-flex gap-2 mb-3">
                <${Button} buttonType="tertiary" css="btn-sm p-0" label=${{ text: 'Select all' }} onClick=${selectAll} />
                <span class="text-muted">·</span>
                <${Button} buttonType="tertiary" css="btn-sm p-0" label=${{ text: 'Clear' }} onClick=${clearAll} />
            </div>

            <div class="consolidated-export-years d-flex flex-column gap-2">
                ${availableYears.map(
                    (year) => html`
                        <div
                            key=${year}
                            class="d-flex align-items-center py-1 ${selectedYears.includes(year) ? 'fw-semibold' : ''}"
                        >
                            <${Checkbox}
                                id=${`export-year-${year}`}
                                field=${{ name: `export-year-${year}`, value: selectedYears.includes(year), setValue: () => toggleYear(year) }}
                                label=${{ text: String(year) }}
                            />
                        </div>
                    `
                )}
            </div>

            ${
                selectedYears.length > 0
                    ? html`
                          <p class="text-muted small mt-3 mb-0">
                              Each sheet will contain September and December SARB average ratings
                              for the consolidated legal-entity view.
                          </p>
                      `
                    : ''
            }
        </${Modal}>
    `;
};
